/** One checkpoint result within a measurement's summary. Which fields are
 *  populated depends on the measurement type and the config that was sent. */
export interface SummaryCheckpoint {
  /** The requested threshold — MPa for strength checkpoints, days for day/window checkpoints. */
  checkpoint: number;
  reached: boolean;
  /** Index into the measurement's time series where the checkpoint was first met, or null. */
  index: number | null;
  strength?: number | null;
  timeReached?: string | Date | null;
  timeDiffHours?: number | null;
  avgTemp?: number | null;
  maxTemp?: number | null;
  minTemp?: number | null;
  /** Extra: max temperature from series start until the checkpoint was reached. */
  maxTempAtReach?: number | null;
}

/** Per-spec-value pass/fail for a maturity measurement, vs the measurement's own `specifications`. */
export interface SummarySpecComparison {
  spec: number;
  met: boolean;
  at?: string | Date | null;
}

export interface SummaryRow {
  _id: string;
  name: string;
  stageName: string | null;
  measurementType: string;
  startTime: string | Date | null;
  maxTemp?: number;
  minTemp?: number;
  avgTemp?: number;
  strengthCheckpoints?: SummaryCheckpoint[];
  daysCheckpoints?: SummaryCheckpoint[];
  averageCheckpoints?: SummaryCheckpoint[];
  specComparison?: SummarySpecComparison[];
}

export type SummaryTypeResult =
  | { supported: true; config: SummaryTypeConfig; measurements: SummaryRow[] }
  | { supported: false };

/** Keyed by measurementType string. */
export type SummaryResponse = Record<string, SummaryTypeResult>;

// ---- request config ----

export interface MaturitySummaryTypeConfig {
  strengthCheckpoints?: number[];
  daysCheckpoints?: number[];
  /** e.g. ['maxTempAtReach'] */
  strengthCheckpointExtras?: string[];
  /** advanced — requires monitoring.summarize.advanced_options */
  includeSpecComparison?: boolean;
}

export interface TemperatureSummaryTypeConfig {
  averageCheckpoints?: number[];
}

export type SummaryTypeConfig =
  | MaturitySummaryTypeConfig
  | TemperatureSummaryTypeConfig
  | Record<string, unknown>;

/** Body of POST /measurements/summary and /summary/xlsx. Keyed by measurementType. */
export interface SummaryConfig {
  config: Record<string, SummaryTypeConfig>;
}

/** Config field keys that require the advanced entitlement. Checked on the backend. */
export const ADVANCED_SUMMARY_CONFIG_KEYS = ['includeSpecComparison'] as const;
